/**
 * Geofence Service
 *
 * Provides point-in-polygon (PIP) validation using ray-casting algorithm
 * to determine if GPS coordinates fall within configured geofence boundaries.
 * This is a critical security function for location-based access control.
 */

import type { Coordinate, GeofenceError } from '@/types/geofence'
import { ACCEPTABLE_GPS_ACCURACY } from '@/utils/constants'
import { logger, LogLevel } from '@/services/logger'

// Track bypass state to avoid log spam
let lastBypassState: boolean | null = null

/**
 * Validation result from geofence validation
 */
export interface ValidationResult {
    isValid: boolean // Overall validation result (accurate AND inside)
    isAccurate: boolean // GPS accuracy ≤50 meters
    isInsideGeofence: boolean // Point is inside polygon
    message: string // User-friendly message explaining result
}

/**
 * Geofence validation result with error handling
 */
export interface GeofenceValidationResult {
    isValid: boolean
    isAccurate: boolean
    isInsideGeofence: boolean
    message: string
    error?: GeofenceError
}

/**
 * Determines if a point is inside a polygon using ray-casting algorithm
 *
 * The ray-casting algorithm works by casting a horizontal ray from the point
 * to infinity and counting how many polygon edges the ray crosses:
 * - Odd number of crossings: Point is inside the polygon
 * - Even number of crossings: Point is outside the polygon
 *
 * @param point - The coordinate to test
 * @param polygon - Array of polygon vertices (must be closed)
 * @returns true if point is inside polygon, false otherwise
 */
export function isPointInPolygon(point: Coordinate, polygon: Coordinate[]): boolean {
    // Performance logging in development mode
    const startTime = import.meta.env.DEV ? performance.now() : 0

    // Handle edge cases for invalid polygons
    if (!polygon || polygon.length < 3) {
        if (import.meta.env.DEV) {
            console.warn('Invalid polygon: must have at least 3 points', polygon)
        }
        return false
    }

    const { lat: x, lng: y } = point
    let crossings = 0

    // Cast a horizontal ray from the point to the right (+x direction)
    // Count how many polygon edges intersect the ray
    for (let i = 0; i < polygon.length; i++) {
        const current = polygon[i]
        const next = polygon[(i + 1) % polygon.length] // Wrap to first point for closed polygon

        const { lat: x1, lng: y1 } = current
        const { lat: x2, lng: y2 } = next

        // Check if point is on this edge first
        if (isPointOnEdge(x, y, x1, y1, x2, y2)) {
            return true // Point is on boundary, consider it inside
        }

        // Check if the ray intersects this edge
        if (rayIntersectsEdge(x, y, x1, y1, x2, y2)) {
            crossings++
        }
    }

    // Point is inside if ray crosses an odd number of edges
    const result = crossings % 2 === 1

    // Log performance in development mode
    if (import.meta.env.DEV) {
        const endTime = performance.now()
        const duration = endTime - startTime
        console.log(
            `PIP validation took ${duration.toFixed(2)}ms for ${polygon.length}-point polygon`
        )
    }

    return result
}

/**
 * Checks if a horizontal ray from point (x,y) intersects with an edge
 *
 * @param x - Point x coordinate (latitude)
 * @param y - Point y coordinate (longitude)
 * @param x1 - Edge start x coordinate
 * @param y1 - Edge start y coordinate
 * @param x2 - Edge end x coordinate
 * @param y2 - Edge end y coordinate
 * @returns true if ray intersects edge
 */
function rayIntersectsEdge(
    x: number,
    y: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
): boolean {
    // Edge case: point lies exactly on a vertex
    if (
        (Math.abs(x - x1) < 1e-10 && Math.abs(y - y1) < 1e-10) ||
        (Math.abs(x - x2) < 1e-10 && Math.abs(y - y2) < 1e-10)
    ) {
        return true
    }

    // Edge case: point lies exactly on the edge
    if (isPointOnEdge(x, y, x1, y1, x2, y2)) {
        return true
    }

    // Handle horizontal edges (y1 == y2) - point on horizontal edge
    if (Math.abs(y1 - y2) < 1e-10 && Math.abs(y - y1) < 1e-10) {
        // Point is on horizontal edge, check if it's within the edge bounds
        const minX = Math.min(x1, x2)
        const maxX = Math.max(x1, x2)
        return x >= minX && x <= maxX
    }

    // Ray must be horizontal and go to the right
    // Edge must cross the ray's y-coordinate
    if ((y1 <= y && y < y2) || (y2 <= y && y < y1)) {
        // Calculate x-coordinate where edge intersects the ray
        const intersectionX = x1 + ((y - y1) * (x2 - x1)) / (y2 - y1)

        // Ray intersects if intersection is to the right of the point
        return intersectionX > x
    }

    return false
}

/**
 * Checks if a point lies exactly on an edge (within tolerance)
 *
 * @param x - Point x coordinate
 * @param y - Point y coordinate
 * @param x1 - Edge start x coordinate
 * @param y1 - Edge start y coordinate
 * @param x2 - Edge end x coordinate
 * @param y2 - Edge end y coordinate
 * @returns true if point is on edge
 */
function isPointOnEdge(
    x: number,
    y: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
): boolean {
    // Check if point is within the edge's bounding box
    const minX = Math.min(x1, x2)
    const maxX = Math.max(x1, x2)
    const minY = Math.min(y1, y2)
    const maxY = Math.max(y1, y2)

    if (x < minX || x > maxX || y < minY || y > maxY) {
        return false
    }

    // Handle horizontal edges (y1 == y2)
    if (Math.abs(y1 - y2) < 1e-10) {
        return Math.abs(y - y1) < 1e-10
    }

    // Handle vertical edges (x1 == x2)
    if (Math.abs(x1 - x2) < 1e-10) {
        return Math.abs(x - x1) < 1e-10
    }

    // Check if point lies on the line (within tolerance)
    const tolerance = 1e-10
    const distance =
        Math.abs((y2 - y1) * x - (x2 - x1) * y + x2 * y1 - y2 * x1) /
        Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2)

    return distance < tolerance
}

/**
 * Validates if GPS accuracy is acceptable for geofence validation
 *
 * @param accuracy - GPS accuracy in meters
 * @returns true if accuracy ≤50 meters, false otherwise
 */
export function isAccuracyAcceptable(accuracy: number): boolean {
    return accuracy <= ACCEPTABLE_GPS_ACCURACY
}

/**
 * Validates location against geofence with accuracy and PIP checks
 *
 * @param coords - Current GPS coordinates
 * @param accuracy - GPS accuracy in meters
 * @param geofence - Geofence polygon points
 * @param bypassGeofence - Optional debug mode flag to bypass geofence check
 * @returns ValidationResult with detailed validation information
 */
export function validateLocation(
    coords: Coordinate,
    accuracy: number,
    geofence: Coordinate[],
    bypassGeofence = false
): ValidationResult {
    // Check if debug mode bypass is enabled
    if (bypassGeofence) {
        // Only log when bypass state changes (to avoid spam)
        if (lastBypassState !== true) {
            logger.logAdmin(
                LogLevel.WARN,
                'location',
                'DEBUG MODE: Geofence check bypassed - location validation skipped',
                {
                    coordinates: coords,
                    accuracy,
                    debugMode: 'geofence_bypass_enabled',
                    timestamp: new Date().toISOString()
                }
            )
            lastBypassState = true
        }

        return {
            isValid: true,
            isAccurate: true,
            isInsideGeofence: true,
            message: 'DEBUG MODE: Geofence check bypassed'
        }
    }

    // Log when bypass is turned off
    if (lastBypassState === true) {
        logger.logAdmin(
            LogLevel.INFO,
            'location',
            'DEBUG MODE: Geofence check re-enabled - location validation active',
            {
                timestamp: new Date().toISOString()
            }
        )
        lastBypassState = false
    }

    // Check GPS accuracy
    const isAccurate = isAccuracyAcceptable(accuracy)

    // Always check if point is inside geofence, regardless of accuracy
    const isInside = isPointInPolygon(coords, geofence)

    // If accuracy is poor, fail validation even if inside geofence
    if (!isAccurate) {
        return {
            isValid: false,
            isAccurate: false,
            isInsideGeofence: isInside,
            message: `GPS accuracy is ${accuracy.toFixed(0)}m, but must be ≤${ACCEPTABLE_GPS_ACCURACY}m for geofence validation`
        }
    }

    // Good accuracy but outside geofence
    if (!isInside) {
        return {
            isValid: false,
            isAccurate: true,
            isInsideGeofence: false,
            message:
                'You are outside the designated work area. Please move to the correct location.'
        }
    }

    // Both checks passed
    return {
        isValid: true,
        isAccurate: true,
        isInsideGeofence: true,
        message: 'Location validated successfully. You are in the correct work area.'
    }
}

/**
 * Validates location against geofence with comprehensive error handling
 *
 * @param coords - Current GPS coordinates
 * @param accuracy - GPS accuracy in meters
 * @param geofence - Geofence polygon points
 * @param bypassGeofence - Optional debug mode flag to bypass geofence check
 * @returns GeofenceValidationResult with detailed validation information and error handling
 */
export function validateLocationWithErrorHandling(
    coords: Coordinate,
    accuracy: number,
    geofence: Coordinate[],
    bypassGeofence = false
): GeofenceValidationResult {
    // Check if debug mode bypass is enabled
    if (bypassGeofence) {
        // Only log when bypass state changes (to avoid spam)
        if (lastBypassState !== true) {
            logger.logAdmin(
                LogLevel.WARN,
                'location',
                'DEBUG MODE: Geofence check bypassed - location validation skipped',
                {
                    coordinates: coords,
                    accuracy,
                    debugMode: 'geofence_bypass_enabled',
                    timestamp: new Date().toISOString()
                }
            )
            lastBypassState = true
        }

        return {
            isValid: true,
            isAccurate: true,
            isInsideGeofence: true,
            message: 'DEBUG MODE: Geofence check bypassed'
        }
    }

    // Log when bypass is turned off
    if (lastBypassState === true) {
        logger.logAdmin(
            LogLevel.INFO,
            'location',
            'DEBUG MODE: Geofence check re-enabled - location validation active',
            {
                timestamp: new Date().toISOString()
            }
        )
        lastBypassState = false
    }

    // Debug logging for validation start
    console.log('[GeofenceService] Validation started:', {
        timestamp: new Date().toISOString(),
        coordinates: { lat: coords.lat, lng: coords.lng },
        accuracy,
        geofencePoints: geofence?.length || 0
    })

    // Check for missing geofence configuration
    if (!geofence || geofence.length === 0) {
        const error = createMissingConfigurationError()
        console.log('[GeofenceService] Missing configuration error:', {
            timestamp: new Date().toISOString(),
            error: error.error,
            userMessage: error.userMessage
        })

        return {
            isValid: false,
            isAccurate: false,
            isInsideGeofence: false,
            message: error.userMessage,
            error
        }
    }

    // Check for invalid polygon data
    if (geofence.length < 3) {
        const error = createInvalidPolygonError(geofence.length)
        console.log('[GeofenceService] Invalid polygon error:', {
            timestamp: new Date().toISOString(),
            pointCount: geofence.length,
            error: error.error,
            userMessage: error.userMessage
        })

        return {
            isValid: false,
            isAccurate: false,
            isInsideGeofence: false,
            message: error.userMessage,
            error
        }
    }

    // Check GPS accuracy
    const isAccurate = isAccuracyAcceptable(accuracy)

    // Always check if point is inside geofence, regardless of accuracy
    const isInside = isPointInPolygon(coords, geofence)

    // If accuracy is poor, fail validation even if inside geofence
    if (!isAccurate) {
        const result = {
            isValid: false,
            isAccurate: false,
            isInsideGeofence: isInside,
            message: `GPS accuracy is ${accuracy.toFixed(0)}m, but must be ≤${ACCEPTABLE_GPS_ACCURACY}m for geofence validation`
        }

        console.log('[GeofenceService] Accuracy insufficient:', {
            timestamp: new Date().toISOString(),
            accuracy,
            threshold: ACCEPTABLE_GPS_ACCURACY,
            isInsideGeofence: isInside,
            result: 'accuracy_insufficient'
        })

        return result
    }

    // Good accuracy but outside geofence
    if (!isInside) {
        const result = {
            isValid: false,
            isAccurate: true,
            isInsideGeofence: false,
            message:
                'You are outside the designated work area. Please move to the correct location.'
        }

        console.log('[GeofenceService] Outside geofence:', {
            timestamp: new Date().toISOString(),
            coordinates: { lat: coords.lat, lng: coords.lng },
            result: 'outside_geofence'
        })

        // Log security event for geofence failure
        logger
            .logSecurity(
                LogLevel.WARN,
                'location',
                'Worker attempted check-in from outside designated work area',
                'geofence_failure',
                {
                    location: { lat: coords.lat, lng: coords.lng },
                    failureReason: 'GPS coordinates outside geofence boundary'
                }
            )
            .catch(error => {
                console.error('Failed to log geofence security event:', error)
            })

        return result
    }

    // Both checks passed
    const result = {
        isValid: true,
        isAccurate: true,
        isInsideGeofence: true,
        message: 'Location validated successfully. You are in the correct work area.'
    }

    console.log('[GeofenceService] Validation successful:', {
        timestamp: new Date().toISOString(),
        coordinates: { lat: coords.lat, lng: coords.lng },
        accuracy,
        result: 'success'
    })

    return result
}

/**
 * Create missing configuration error
 * @returns GeofenceError for missing configuration
 */
export function createMissingConfigurationError(): GeofenceError {
    return {
        error: 'CONFIG_MISSING',
        message: 'Geofence configuration is missing or invalid',
        userMessage: 'Configuration error. Contact IT support.',
        action: 'Contact your IT administrator to configure the work area',
        severity: 'error'
    }
}

/**
 * Create invalid polygon error
 * @param pointCount - Number of points in the polygon
 * @returns GeofenceError for invalid polygon
 */
export function createInvalidPolygonError(pointCount: number): GeofenceError {
    return {
        error: 'INVALID_POLYGON',
        message: `Geofence polygon has ${pointCount} points, but requires at least 3 points`,
        userMessage: 'Configuration error. Contact IT support.',
        action: 'Contact your IT administrator to fix the work area configuration',
        severity: 'error'
    }
}
